/**
 * Clues, said by the person hiding.
 *
 * "10-50 km" is a database cell. "I'm 10–50 km from the ocean" is somebody
 * taunting you from somewhere. Same information, completely different game —
 * and it costs nothing but this file, because the stored answer never changes.
 *
 * Everything here is presentation. The canonical answer in `attempt_clues`
 * stays exactly as the engine wrote it, so constraints and pricing are
 * untouched.
 */

const dash = (band: string) => band.replace(/(\d)-(\d)/g, "$1–$2");

/** How a bought clue reads on the card. `who` is the hider's first name. */
export function phraseClue(
  type: string,
  answer: string,
  who: string | null,
): string {
  const me = who ?? "I";
  const iAm = who ? `${who} is` : "I'm";

  switch (type) {
    case "hemisphere":
      return `${iAm} in the ${answer} hemisphere.`;
    case "continent":
      return `${iAm} somewhere in ${answer}.`;
    case "country":
      return `${iAm} in ${answer}.`;
    case "language":
      return `They speak ${answer} where ${who ? who : "I am"}.`;
    case "coast":
      return answer.startsWith("0")
        ? `${iAm} right on the coast — under 10 km from the sea.`
        : `${iAm} ${dash(answer)} from the sea.`;
    case "population":
      return `About ${dash(answer)} people live around ${who ? who : "me"}.`;
    case "localtime":
      return `It's ${answer} here right now.`;
    case "initial":
      return `The name of this place starts with ${answer}.`;
    case "elevation":
      return answer.startsWith("0")
        ? `${iAm} basically at sea level.`
        : `${iAm} ${dash(answer)} above sea level.`;
    case "driving":
      return `They drive on the ${answer.toLowerCase()} here.`;
    case "metro":
      return answer.toLowerCase().includes("one")
        ? `${iAm} in a city of over a million people.`
        : `The nearest million-person city is ${dash(answer)} away.`;
    case "biome":
      return `The land around ${who ? who : "me"} is ${answer.toLowerCase()}.`;
    case "heritage":
      return answer.startsWith("under")
        ? `There's a World Heritage site within 10 km of ${me}.`
        : `The nearest World Heritage site is ${dash(answer)} away.`;
    case "bearing":
      return `${iAm} ${compass(answer)} of your last guess.`;
    default:
      // A hider-written note is already in their own words.
      return answer;
  }
}

const COMPASS_WORDS: Record<string, string> = {
  N: "due north",
  NE: "north-east",
  E: "due east",
  SE: "south-east",
  S: "due south",
  SW: "south-west",
  W: "due west",
  NW: "north-west",
};

const compass = (a: string) => COMPASS_WORDS[a] ?? a;

/**
 * The line after a wrong guess, by warmth band. Naming the hider is the whole
 * point — you are not missing a coordinate, you are missing a person.
 */
export function missLine(band: number, who: string | null): string {
  const them = who ?? "them";
  // The band is already the headline; this is the reaction to it.
  const lines = [
    `Found ${them}.`,
    `Agonising. You were almost on top of ${them}.`,
    `Close. You can nearly smell ${them}.`,
    `Right area, wrong spot.`,
    `You have the general region, at least.`,
    `${them} is a long way from there.`,
    `${them} is nowhere near there.`,
  ];
  return lines[Math.min(lines.length - 1, Math.max(0, band))];
}

/** The line on the reveal when the round is lost. */
export function foundLine(guessesUsed: number, who: string | null): string {
  const them = who ?? "them";
  if (guessesUsed === 1) return `Found ${them} first try.`;
  return `Found ${them} on guess ${guessesUsed}.`;
}
